import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import Script from 'next/script'
import { SiteCodes } from 'shared-types'
import { Image } from '~/components/Image'
import { usePage, useZendesk } from '~/hooks'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './ZendeskLauncher.module.scss'

const ZENDESK_CHAT_MAIN_OFFSET = 32
const ZENDESK_LAUNCHER_MAIN_OFFSET = 16
const ZENDESK_ON_EVENT = 'messenger:on'

const ZendeskLauncher = () => {
  const { siteCode } = usePage()
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0)
  const zendeskLauncherButtonRef = useRef<HTMLButtonElement | null>(null)
  const {
    zendeskChatIframe,
    setZendeskChatIframe,
    zendeskLauncherButton,
    setZendeskLauncherButton,
    isOpen,
    setIsOpen,
    zendeskBottomOffset,
    zendeskLauncherHeight,
    setZendeskLauncherHeight,
  } = useZendesk()
  const isRtm = siteCode === SiteCodes.RTM

  const zendeskId = useMemo(() => {
    if (siteCode === SiteCodes.RTM) {
      return process.env.NEXT_PUBLIC_ZENDESK_ID_RTM || ''
    }

    if (siteCode === SiteCodes.JaycarNz) {
      return process.env.NEXT_PUBLIC_ZENDESK_ID_NZ || ''
    }

    if (siteCode === SiteCodes.JaycarAu) {
      return process.env.NEXT_PUBLIC_ZENDESK_ID_AU || ''
    }

    return ''
  }, [siteCode])

  useEffect(() => {
    let observer: MutationObserver

    if (zendeskChatIframe == null) {
      observer = new MutationObserver(() => {
        const tmpZendeskChatIframe = document.querySelector(
          'iframe[name="Messaging window"]'
        ) as HTMLIFrameElement

        if (tmpZendeskChatIframe != null) {
          tmpZendeskChatIframe.classList.add(styles.zendeskChat)
          setZendeskChatIframe(tmpZendeskChatIframe)
        }

        if (zendeskLauncherButtonRef.current != null) {
          setZendeskLauncherButton(zendeskLauncherButtonRef.current)
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      observer?.disconnect()
    }
  }, [
    setZendeskChatIframe,
    setZendeskLauncherButton,
    zendeskChatIframe,
    zendeskLauncherButton,
  ])

  useEffect(() => {
    let buttonSizeObserver: ResizeObserver

    if (zendeskLauncherButton != null) {
      buttonSizeObserver = new ResizeObserver(() => {
        setZendeskLauncherHeight(
          zendeskLauncherButton.getBoundingClientRect().height
        )
      })

      buttonSizeObserver.observe(zendeskLauncherButton)
    }

    return () => {
      buttonSizeObserver?.disconnect()
    }
  }, [setZendeskLauncherHeight, zendeskLauncherButton])

  useEffect(() => {
    if (zendeskChatIframe == null) {
      return
    }

    const zendeskChatBottomOffset =
      zendeskBottomOffset + ZENDESK_CHAT_MAIN_OFFSET

    zendeskChatIframe.parentElement.style.setProperty(
      '--zendesk-chat-bottom-offset',
      `${zendeskLauncherHeight + zendeskChatBottomOffset}px`
    )
    zendeskChatIframe.parentElement.style.setProperty(
      '--zendesk-bottom-offset',
      `${zendeskBottomOffset}px`
    )
  }, [zendeskChatIframe, zendeskLauncherHeight, zendeskBottomOffset])

  const toggleChatWindow = () => {
    if (zendeskChatIframe != null) {
      window?.zE('messenger', isOpen ? 'close' : 'open', '')
    }
  }

  if (!zendeskId) {
    return null
  }

  const renderUnreadMessagesPill = () => {
    if (!totalUnreadMessages || isOpen || !zendeskLauncherHeight) {
      return null
    }

    const label = totalUnreadMessages > 99 ? '99+' : `${totalUnreadMessages}`

    return (
      <div
        className={classNames(
          'absolute rounded-full overflow-hidden',
          'h-6 w-6',
          'flex items-center justify-center',
          styles.unreadMessagesPill,
          {
            'left-1 -top-4 2sm:-top-3': !isRtm,
            'left-0 top-3 2sm:left-2': isRtm,
          }
        )}>
        <Typography
          tag={TypographyTag.div}
          variant={TypographyVariant.BodySmallBold}
          className='!text-white'>
          {label}
        </Typography>
      </div>
    )
  }

  const renderPersonImage = () => {
    if (isRtm) {
      return null
    }

    return (
      <Image
        isRawImage
        src='/images/zendesk-launcher-jc-person.png'
        className={classNames(
          'w-24 absolute bottom-full',
          styles.zendeskPerson
        )}
      />
    )
  }

  const renderCtaImage = () => {
    const imageUrl = isRtm
      ? '/images/zendesk-launcher-rtm-cta.png'
      : '/images/zendesk-launcher-jc-cta.png'

    return (
      <Image
        src={imageUrl}
        className={classNames('w-24 overflow-hidden', {
          'rounded-l-full': !isRtm,
        })}
      />
    )
  }

  const renderCloseButton = () => {
    return (
      <div
        className='absolute right-0 top-0 bottom-0 w-5 overflow-hidden'
        style={
          {
            '--close-button-offset': isRtm ? '12%' : '0',
          } as React.CSSProperties
        }>
        <div
          className={classNames(
            'absolute inset-0 flex items-center justify-center',
            styles.zendeskChevronIcon
          )}>
          <Image
            isRawImage
            src='/images/icons/close-white.svg'
            className='w-3.5'
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <Script
        src={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskId}`}
        id='ze-snippet'
        strategy='lazyOnload'
        onLoad={() => {
          /* open and close the Zendesk chat so the iframe gets injected into the DOM */
          window?.zE('messenger', 'open', '')
          window?.zE('messenger', 'close', '')
          window?.zE('messenger:set', 'zIndex', Number('120'))
          window?.zE(ZENDESK_ON_EVENT, 'open', () => {
            setIsOpen(true)
          })
          window?.zE(ZENDESK_ON_EVENT, 'close', () => {
            setIsOpen(false)
          })
          window?.zE(
            ZENDESK_ON_EVENT,
            'unreadMessages',
            (totalUnreadMessages: number) => {
              setTotalUnreadMessages(totalUnreadMessages)
            }
          )
        }}
      />
      <div
        style={
          {
            '--zendesk-launcher-bottom-offset': `${
              zendeskBottomOffset + ZENDESK_LAUNCHER_MAIN_OFFSET
            }px`,
          } as React.CSSProperties
        }
        className={classNames('fixed right-0 z-[121]', styles.zendeskLauncher, {
          [styles.isOpen]: isOpen,
        })}>
        <button
          ref={zendeskLauncherButtonRef}
          type='button'
          className='block'
          onClick={toggleChatWindow}>
          <div className='relative'>
            {renderPersonImage()}
            <div className='relative'>
              {renderUnreadMessagesPill()}
              {renderCtaImage()}
              {renderCloseButton()}
            </div>
          </div>
        </button>
      </div>
    </>
  )
}

export default ZendeskLauncher
